import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { subscribeToMediaAssets } from '../services/media';
import type { MediaAsset } from '../types/media';

type MediaMap = Record<string, MediaAsset[]>;

type MediaContextValue = {
  assets: MediaAsset[];
  getByPlacement: (placement: string) => MediaAsset[];
  getFirstByPlacement: (placement: string) => MediaAsset | undefined;
};

const MediaContext = createContext<MediaContextValue | undefined>(undefined);

const toPlacementMap = (assets: MediaAsset[]): MediaMap => {
  const map: MediaMap = {};
  for (const asset of assets) {
    const key = asset.placement ?? '';
    if (!map[key]) map[key] = [];
    map[key].push(asset);
  }
  return map;
};

interface MediaProviderProps {
  children: ReactNode;
}

export const MediaProvider = ({ children }: MediaProviderProps) => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeToMediaAssets(
      (items) => {
        setAssets(items);
      },
      (error) => {
        console.warn('Media subscription error', error);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  const placementMap = useMemo(() => toPlacementMap(assets), [assets]);

  const getByPlacement = useMemo(
    () => (placement: string) => placementMap[placement] ?? [],
    [placementMap],
  );

  const getFirstByPlacement = useMemo(
    () => (placement: string) => (placementMap[placement] ?? [])[0],
    [placementMap],
  );

  const value: MediaContextValue = useMemo(
    () => ({ assets, getByPlacement, getFirstByPlacement }),
    [assets, getByPlacement, getFirstByPlacement],
  );

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
};

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (!context) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
};

